
// --- Vue & Template imports ---
import { Component, Prop, Vue } from 'vue-property-decorator';

// --- Models ---
import { ReviewSetting, ReviewType } from '@/models/Settings.model';

// --- Services ---
import AppService from '@/services/app';
import SettingsService from '@/services/settings';

// --- Third Party ---

@Component
export default class ReviewSettings extends Vue {
  // used by Filter Area
  search: string = ''; // !!TODO Do we really need a search here? Like how many of these are they likely to have??

  // used by No Data Slot
  isLoading: boolean = true;

  // used by Create/Update Item Dialog
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: ReviewSetting = {
    id: 0,
    reviewTypeId: 0,
    reviewType: '',
    customerVariable: '',
    createdAt: '',
  };

  reviewTypesToAdd: ReviewType[] = [];
  selectedReviewType: ReviewType = {
    id: 0,
    logoUrl: '',
    name: '',
  };

  valid: boolean = false;
  clicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
  };

  // used by Confirm Action Dialog for Delete Item
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Type',
          value: 'reviewType',
        },
        {
          text: 'Identifier',
          value: 'customerVariable',
        },
        {
          text: 'Created At',
          value: 'createdAt',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async beforeMount() {
      await SettingsService.listReviewTypes(false);
      await this.listAll();
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await SettingsService.listAll(true);
        this.reviewTypesToAdd = [];

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        } else {
          // now reduce review type options
          if (this.$store.getters['settings/settings'] && this.$store.getters['settings/reviewSettings'].length > 0) {
            this.reviewTypesToAdd = this.$store.getters['settings/reviewTypes'].filter((item: ReviewType) => {
              const found: number = this.$store.getters['settings/reviewSettings'].findIndex((setting: ReviewSetting) => setting.reviewTypeId === item.id);
              return found < 0; // did not find, so they can still add thie review type
            });
          } else {
            this.reviewTypesToAdd = this.$store.getters['settings/reviewTypes'];
          }
        }

      } finally {
        this.isLoading = false;
      }
    }
  // #endregion

  // #region Functions required for the Create/Update Item Dialog
    openNewTab(url: string) {
      window.open(url, '_blank');
    }

    async launchNewItemDialog() {
      this.clickedItem = {
        id: 0,
        reviewTypeId: 0,
        reviewType: '',
        customerVariable: '',
        createdAt: '',
      };

      this.selectedReviewType = {
        id: 0,
        logoUrl: '',
        name: '',
      };

      this.valid = false;
      this.title = 'Create New Review Type';

      this.$nextTick(() => {
        if (this.$refs.itemForm) {
          (this.$refs.itemForm as any).resetValidation();
        }
      });

      this.showItemDialog = true;
    }

    async launchUpdateItemDialog(item: ReviewSetting) {
      this.clickedItem = Object.assign({}, item);
      this.title = 'Update Review Type';

      this.$nextTick(() => {
        if (this.$refs.itemForm) {
          (this.$refs.itemForm as any).resetValidation();
        }
      });

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await SettingsService.create({
          reviewTypeId: this.selectedReviewType.id,
          customerVariable: this.clickedItem.customerVariable,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Settings.vue - doCreate - 391 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await SettingsService.update({
          id: this.clickedItem.id,
          customerVariable: this.clickedItem.customerVariable,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }


      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Settings.vue - doUpdate - 391 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
    // !!TODO: Do we want to add Suspend / Activate ?  #later
    
    async launchDeleteItemDialog(item: ReviewSetting) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> your settings for <span style="font-weight: 700;">' + this.clickedItem.reviewType + '</span>.<br/>'
        + 'We will no longer import reviews from this site.<br/><br/> Are you sure? ';
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();        
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await SettingsService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Settings.vue - doDelete - 1289 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion

}
